import React, { useState, useEffect } from 'react';
import { uploadFileToS3 } from 'util/helpers';
import {
  Form,
  Input,
  Button,
  Select,
  InputNumber,
  Spin,
  Alert,
  Typography,
  Switch,
} from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { SIDE_MENU_EVENTS } from '@src/components/menu/sideMenu';
import styles from './3d-generation.module.css';
import { useSession } from 'next-auth/react';
import {
  useCheckTaskStatusQuery,
  useGenerateTextTo3DTripoMutation,
  useCreateOneGeneratedAssetsMutation,
} from '@src/components/generated/graphql';

const { TextArea } = Input;
const { Title, Text } = Typography;
const { Option } = Select;

type GLBSceneObject = {
  name: string;
  url: string;
};

export interface Tripo3DGenerationProps {
  addToScene: (data: any) => void;
}

const PREVIEW_FALLBACK_IMAGE =
  'https://d23hwblhugcea5.cloudfront.net/studioAssets/images/img1.jpg';

const Tripo3DGeneration: React.FC<Tripo3DGenerationProps> = ({
  addToScene,
}) => {
  const [form] = Form.useForm();
  const session = useSession();
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [glbSceneObject, setGlbSceneObject] = useState<GLBSceneObject | null>(
    null
  );
  const [taskId, setTaskId] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const [createGeneratedAsset] = useCreateOneGeneratedAssetsMutation();
  const [generateTextTo3DTripo, { loading: generatingModel }] =
    useGenerateTextTo3DTripoMutation({
      onCompleted: (data) => {
        if (data?.generateTextTo3DTripo?.task_id) {
          setTaskId(data.generateTextTo3DTripo.task_id);
          setIsPolling(true);
        } else if (data?.generateTextTo3DTripo?.error) {
          setError(data.generateTextTo3DTripo.error);
        }
      },
      onError: (error) => {
        setError(`Error initiating generation: ${error.message}`);
      },
    });

  async function uploadFileToStorage(url: string, fileName: string) {
    const blob = await fetch('/api/get-blob', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ modelUrl: url }),
    }).then((res) => res.blob());

    if (!blob) {
      return null;
    }

    const file = new File([blob], fileName);

    return await uploadFileToS3(
      file,
      session.data?.user?.email ?? 'no-email',
      '3d-generation'
    );
  }

  const { loading: checkingStatus, refetch: checkStatus } =
    useCheckTaskStatusQuery({
      skip: !taskId,
      variables: { taskId: taskId || '' },
      fetchPolicy: 'network-only',
      onCompleted: async (data) => {
        const statusData = data?.checkTaskStatus;

        setResult(statusData);

        if (statusData?.status === 'success') {
          setIsPolling(false);

          let modelUrl: string | null = null;
          if (statusData?.output?.pbr_model) {
            modelUrl = statusData.output.pbr_model;
          } else if (statusData?.output?.model) {
            modelUrl = statusData.output.model;
          } else if (statusData?.output?.base_model) {
            modelUrl = statusData.output.base_model;
          }

          if (!modelUrl) {
            return;
          }

          const fileName = `tripo-${
            statusData.task_id
          }-${new Date().toUTCString()}.glb`;
          const assetUrl = await uploadFileToStorage(modelUrl, fileName);
          const userId = session.data?.user?.id;

          if (!assetUrl) {
            setError(`Generation failed`);
            setIsPolling(false);
            return;
          }

          if (userId) {
            const tripo3dPreviewUrl = statusData?.output?.rendered_image;

            const fileNamePreview = `tripo-${
              statusData.task_id
            }-${new Date().toUTCString()}-preview-image.webp`;

            const previewUrl = tripo3dPreviewUrl
              ? await uploadFileToStorage(tripo3dPreviewUrl, fileNamePreview)
              : PREVIEW_FALLBACK_IMAGE;

            await createGeneratedAsset({
              variables: {
                data: {
                  assetUrl,
                  previewUrl: previewUrl ?? PREVIEW_FALLBACK_IMAGE,
                  userId,
                },
              },
            });
          }

          const newGlbSceneObject = {
            url: assetUrl,
            name: fileName,
          };

          setGlbSceneObject(newGlbSceneObject);
        } else if (statusData?.status === 'failed') {
          setError(`Generation failed: ${statusData.error || 'Unknown error'}`);
          setIsPolling(false);
        } else if (statusData?.status === 'cancelled') {
          setError('Generation was cancelled');
          setIsPolling(false);
        } else if (statusData?.status === 'unknown') {
          setError(
            'Generation status is unknown. Please try again or contact support with your task ID.'
          );
          setIsPolling(false);
        }
      },
    });

  useEffect(() => {
    if (!isPolling || !taskId) return () => {};

    const pollInterval = 10000;
    const poll = () => {
      checkStatus();
    };

    poll();

    const intervalId = setInterval(poll, pollInterval);

    return () => clearInterval(intervalId);
  }, [isPolling, taskId, checkStatus]);

  const handleSubmit = (values: any) => {
    setResult(null);
    setError(null);
    setTaskId(null);
    setGlbSceneObject(null);

    const options: any = {};

    if (values.model_version) {
      options.model_version = values.model_version;
    }

    if (values.negative_prompt) {
      options.negative_prompt = values.negative_prompt;
    }

    if (values.model_seed) {
      options.model_seed = parseInt(values.model_seed, 10);
    }

    if (values.image_seed) {
      options.image_seed = parseInt(values.image_seed, 10);
    }

    if (values.face_limit) {
      options.face_limit = parseInt(values.face_limit, 10);
    }

    if (values.texture) {
      options.texture = values.texture;
    }

    if (values.pbr !== undefined) {
      options.pbr = values.pbr;
    }

    if (values.texture_seed) {
      options.texture_seed = parseInt(values.texture_seed, 10);
    }

    if (values.texture_quality) {
      options.texture_quality = values.texture_quality;
    }

    if (values.auto_size !== undefined) {
      options.auto_size = values.auto_size;
    }

    if (values.style && values.style !== 'default') {
      options.style = values.style;
    }

    // enabling quad force to generate FBX assets
    options.quad = false;

    generateTextTo3DTripo({
      variables: {
        input: {
          prompt: values.prompt,
          options: options,
        },
      },
    });
  };

  const toggleAdvancedOptions = () => {
    setShowAdvancedOptions(!showAdvancedOptions);
  };

  const addToSceneObject = () => {
    if (!glbSceneObject) return;
    addToScene({
      name: glbSceneObject?.name,
      url: glbSceneObject?.url,
      type: SIDE_MENU_EVENTS.LIBRARY_3D,
      index: 0,
    });
  };

  const renderResults = () => {
    if (!result || !result.output) return null;

    return (
      <div className={styles.result}>
        <Title level={4}>Generation Results</Title>

        <>
          {result.output.rendered_image && (
            <>
              <div className={styles.resultTitle}>Preview Image:</div>
              <div>
                <img
                  src={result.output.rendered_image}
                  alt='Preview'
                  className={styles.previewImage}
                  onClick={addToSceneObject}
                />
              </div>
            </>
          )}

          <div className={styles.resultTitle}>Download 3D Models:</div>

          {result.output.model && (
            <a
              href={result.output.model}
              target='_blank'
              rel='noopener noreferrer'
              className={styles.downloadLink}
            >
              Download GLB Model
            </a>
          )}

          {result.output.base_model && (
            <a
              href={result.output.base_model}
              target='_blank'
              rel='noopener noreferrer'
              className={styles.downloadLink}
            >
              Download Base Model
            </a>
          )}

          {result.output.pbr_model && (
            <a
              href={result.output.pbr_model}
              target='_blank'
              rel='noopener noreferrer'
              className={styles.downloadLink}
            >
              Download PBR Model
            </a>
          )}
        </>
        {glbSceneObject ? (
          <div className={styles.addButton}>
            <Button type='primary' onClick={addToSceneObject}>
              Add to Scene
            </Button>
          </div>
        ) : (
          <div className={styles.addButton}>
            <Spin />
            <span>Loading 3D model (you can add it to the scene later)</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.generationContainer}>
      <Form
        form={form}
        layout='vertical'
        onFinish={handleSubmit}
        initialValues={{
          prompt: 'a modern office chair with ergonomic design',
          model_version: 'v2.5-20250123',
          pbr: true,
          auto_size: true,
          quad: false,
          texture_quality: 'standard',
          texture: true,
          face_limit: 1000,
        }}
      >
        <Form.Item
          name='prompt'
          label='Text Prompt'
          rules={[{ required: true, message: 'Please enter a text prompt' }]}
          className={styles.formItem}
        >
          <TextArea
            placeholder='Describe the 3D model you want to generate'
            autoSize={{ minRows: 3, maxRows: 6 }}
          />
        </Form.Item>

        <div className={styles.optionsToggle} onClick={toggleAdvancedOptions}>
          {showAdvancedOptions ? (
            <>
              <UpOutlined /> Hide Advanced Options
            </>
          ) : (
            <>
              <DownOutlined /> Show Advanced Options
            </>
          )}
        </div>

        {showAdvancedOptions && (
          <div className={styles.optionsContainer}>
            <Form.Item
              name='model_version'
              label='Model Version'
              className={styles.formItem}
            >
              <Select placeholder='Select model version'>
                <Option value='v2.5-20250123'>Version 2.5</Option>
                <Option value='v2.0-20240919'>Version 2</Option>
                <Option value='v1.4-20240625'>Version 1.4</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name='negative_prompt'
              label='Negative Prompt'
              className={styles.formItem}
            >
              <Input placeholder='Terms to exclude from generation' />
            </Form.Item>

            <Form.Item
              name='model_seed'
              label='Model Seed'
              className={styles.formItem}
            >
              <InputNumber
                placeholder='Random seed if left empty'
                min={0}
                max={2147483648}
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item
              name='image_seed'
              label='Image Seed'
              className={styles.formItem}
            >
              <InputNumber
                placeholder='Random seed if left empty'
                min={0}
                max={2147483648}
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item
              name='face_limit'
              label='Face Limit'
              className={styles.formItem}
            >
              <InputNumber
                placeholder='Maximum number of faces'
                min={1000}
                max={200000}
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item
              name='texture'
              label='Texture'
              className={styles.formItem}
              valuePropName='checked'
            >
              <Switch />
            </Form.Item>

            <Form.Item
              name='pbr'
              label='PBR (Physically Based Rendering)'
              className={styles.formItem}
              valuePropName='checked'
            >
              <Switch />
            </Form.Item>

            <Form.Item
              name='texture_seed'
              label='Texture Seed'
              className={styles.formItem}
            >
              <InputNumber
                placeholder='Random seed if left empty'
                min={0}
                max={2147483648}
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item
              name='texture_quality'
              label='Texture Quality'
              className={styles.formItem}
            >
              <Select placeholder='Select texture quality'>
                <Option value='standard'>Standard</Option>
                <Option value='detailed'>Detailed (High Resolution)</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name='auto_size'
              label='Auto Size'
              className={styles.formItem}
              valuePropName='checked'
            >
              <Switch />
            </Form.Item>

            <Form.Item name='style' label='Style' className={styles.formItem}>
              <Select placeholder='Select style (default: no style)'>
                <Option value='default'>Default (No Style)</Option>
                <Option value='person:person2cartoon'>Person: Cartoon</Option>
                <Option value='object:clay'>Object: Clay</Option>
                <Option value='object:steampunk'>Object: Steampunk</Option>
                <Option value='animal:venom'>Animal: Venom</Option>
                <Option value='object:barbie'>Object: Barbie</Option>
                <Option value='object:christmas'>Object: Christmas</Option>
                <Option value='gold'>Gold</Option>
                <Option value='ancient_bronze'>Ancient Bronze</Option>
              </Select>
            </Form.Item>
          </div>
        )}

        <Form.Item>
          <Button
            type='primary'
            htmlType='submit'
            loading={generatingModel || (isPolling && checkingStatus)}
          >
            Generate 3D Model
          </Button>
        </Form.Item>
      </Form>

      {(generatingModel || isPolling) && (
        <div className={styles.loading}>
          <Spin />
          <div style={{ marginTop: 12 }}>
            <Text strong>
              {generatingModel
                ? 'Initiating 3D model generation with Tripo3D...'
                : result &&
                  (result.status === 'queued' || result.status === 'running')
                ? `Generating 3D model with Tripo3D... ${
                    result.progress ? `(${result.progress}%)` : ''
                  }`
                : 'Generating 3D model with Tripo3D...'}
            </Text>
            <div style={{ fontSize: 14, color: '#666', marginTop: 8 }}>
              {result && result.status === 'queued'
                ? 'Your request is queued and will start processing soon.'
                : 'This process may take several minutes to complete.'}
            </div>
            {result && result.progress !== undefined && (
              <div style={{ marginTop: 12 }}>
                <div
                  style={{
                    height: 8,
                    backgroundColor: '#f0f0f0',
                    borderRadius: 4,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${result.progress}%`,
                      backgroundColor: '#1890ff',
                      borderRadius: 4,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {error && (
        <Alert
          message='Error'
          description={error}
          type='error'
          showIcon
          style={{ marginTop: 16 }}
        />
      )}

      {renderResults()}
    </div>
  );
};

export default Tripo3DGeneration;
